import Parse from "parse"
import moment from "moment"
import { parseLimitRequest } from "../../utils"

import { PSE_REGEX_HOURS, editableHourToTimestamp, sortPSEByTimeFields, isPSEFromOvernight, PSES_SIDEBAR_MENUS_ENUM, DEFAULT_PSES_STATUS_TAB, PSE_SUBSTATUSES } from "../../utils/productionStepExecution"
import { getMachinesById } from "../machines/machinesManager"
import { PSE_STATUS } from "../../actions/ProductionSteps/ProductionStepExecutions"
import { COLLECTION_NAMES, convertIdsToPointer, convertIdToPointer } from "../../parseUtils"
import dayjs from "dayjs"

export const ProductionStepExecution = Parse.Object.extend("ProductionStepExecutions")

export const getProductionStepExecution = async (
	id,
	toJson = true,
	includes = [
		"ulteriorStep", "priorSteps.step",
		"productionStep.stepComponents.supplierItem",
		"productionStep.kitchenArea",
		"section", "recipe", "lots",
		"productionStepSnapshot.machineType",
		"substitutes.replacementSupplierItem",
		"productionStepSnapshot.stepComponents.supplierItem",
		// for merged pses
		"mergedProductionStepExecutions",
	],
) => {
	try {
		const productionStepExecution = await new Parse.Query(ProductionStepExecution)
			.equalTo("objectId", id)
			.include(includes)
			.first()

		if (!productionStepExecution) {
			throw new Error("No productionStepExecution find")
		}

		return toJson ? productionStepExecution.toJSON() : productionStepExecution
	} catch (e) {
		return Promise.reject(e)
	}
}

export const hasPSEByRecipeAndProductionDateExists = async (
	recipeId,
	productionDate,
) => {
	try {
		const pse = await new Parse.Query(ProductionStepExecution)
			.equalTo("recipe", convertIdToPointer(COLLECTION_NAMES.recipe, recipeId))
			.equalTo("productionDate", productionDate)
			.first()

		return !!pse
	} catch (e) {
		return Promise.reject(e)
	}
}

export const getFilteredProductionStepExecutions = async ({
	filters,
	fieldsToInclude = [],
	fieldsToSelect = [],
	toJSON = true,
	withUnpackingTransformation = true,
	options = {}
}) => {
	try {
		const basicQuery = new Parse.Query(ProductionStepExecution).notEqualTo("deleted", true).notEqualTo("isActive", false)
		let extraPSEs = []
		if (filters.productionDay) {
			const productionDayToTimestamp = moment.utc(filters.productionDay).startOf("day").valueOf()
			basicQuery._orQuery([ // is'nt this too slow?
				new Parse.Query(ProductionStepExecution).equalTo("stepProductionDate", productionDayToTimestamp),
				new Parse.Query(ProductionStepExecution).equalTo("productionDate", productionDayToTimestamp).doesNotExist("stepProductionDate")
			])


			// In what cases do we need to get the previous day's overnight PSEs?
			if (options?.withExtraPrevDayNightPSEs) {
				// --- Retrieve the previous day PSEs made during the night ---
				const prevDay = moment.utc(productionDayToTimestamp).subtract(1, "day").startOf("day").valueOf()
				extraPSEs = await getPrevDayNightPSEs({
					prevDayTimestamp: prevDay,
					fieldsToSelect,
					limit: parseLimitRequest
				})
			}
		}

		if (filters.productionStep) {
			const productionStep = await new Parse.Query(Parse.Object.extend("ProductionStep"))
				.equalTo("objectId", filters.productionStep)
				.first()

			basicQuery.equalTo("productionStep", productionStep)
		}

		if (filters.states && Array.isArray(filters.states) && filters.states.length > 0) {
			basicQuery.containedIn("status", filters.states)
		}

		if (!withUnpackingTransformation) {
			basicQuery.notEqualTo("productionStepSnapshot.transformation", "UNPACKING")
		}

		basicQuery.notEqualTo("productionStepSnapshot.transformation", "TECHNICAL")

		if (filters.kitchenArea) {
			const kitchenAreaPointer = convertIdToPointer("KitchenArea", filters.kitchenArea.objectId)
			basicQuery.equalTo("productionStepSnapshot.kitchenArea", kitchenAreaPointer)
		}

		// ------------------------------------ //
		// ----------- DataGrid --------------- //
		// ------------------------------------ //
		if (filters.dataGrid) {
			if (filters.dataGrid.type) {
				const transformations = filters.dataGrid.type.value || []
				basicQuery.containedIn("productionStepSnapshot.transformation", transformations)
			}

			// the recipe column is a combination of recipe and section
			if (filters.dataGrid.recipe) {
				const ids = filters.dataGrid.recipe.value || []
				const formattedIdsByType = ids.reduce((acc, id) => {
					const [entityId, type] = id.split("-")
					if (type === "section") {
						acc.sections.push(entityId)
					} else {
						acc.recipes.push(entityId)
					}
					return acc
				}, { sections: [], recipes: [] })

				if (formattedIdsByType.sections.length) {
					basicQuery.containedIn("section", convertIdsToPointer("Section", formattedIdsByType.sections))
				}

				if (formattedIdsByType.recipes.length) {
					basicQuery.containedIn("recipe", convertIdsToPointer("Recipe", formattedIdsByType.recipes))
				}
			}

			if (filters.dataGrid.name) {
				const name = filters.dataGrid.name.value || ""
				basicQuery.matches("productionStepSnapshot.name", new RegExp(name, "ig"))
			}

			if (filters.dataGrid.machines) {
				const machine = filters.dataGrid.machines.value || ""
				basicQuery.matches("machinesBatch.machineName", new RegExp(machine, "ig"))
			}
		}

		// TODO: When using the filter from the new design, may be remove !menuId condition
		if (filters.states && Array.isArray(filters.states) && filters.states.length > 0 && !options.selectedSidebarId) {
			basicQuery.containedIn("status", filters.states)
		}

		if (options.selectedSidebarId) {
			switch (options.selectedSidebarId) {
				case PSES_SIDEBAR_MENUS_ENUM.WEIGHING:
					basicQuery.equalTo("endStepWeighing", true)
					break
				case PSES_SIDEBAR_MENUS_ENUM.TASTING:
					basicQuery.equalTo("status", "TO_TEST") // TODO - use an enum later, it's not defined yet 
					break
				case PSES_SIDEBAR_MENUS_ENUM.COOKING:
					basicQuery
						.containedIn("status", ["TODO", "LOCKED", "IN_PROGRESS", "DONE"])
						.notEqualTo("endStepWeighing", true)
					break
				default:
					break
			}
		}

		let query = basicQuery.include(fieldsToInclude)

		// Only apply select if fields are specified
		if (fieldsToSelect.length > 0) query = query.select(fieldsToSelect)

		query.limit(parseLimitRequest)
		if (options.returnQuery) {
			return query
		}
		const mainPSEs = await query.find()
		// Sort main PSEs by time fields
		const sortedMainPSEs = sortPSEByTimeFields(mainPSEs)
		// Sort extra PSEs (from previous day) separately to maintain their chains
		const sortedExtraPSEs = sortPSEByTimeFields(extraPSEs)
		// Combine: extra PSEs first (from previous day), then main PSEs
		const sortedResults = [...sortedExtraPSEs, ...sortedMainPSEs]

		return toJSON ? sortedResults.map(result => result.toJSON()) : sortedResults
	}
	catch (e) {
		console.error("getFilteredProductionStepExecutions error", e)
	}
}

export const getPSEsQuery = ({
	filters = {},
	fieldsToInclude = [],
	selectedSidebarId,
	selectedStatus: selectedStatusValue,
}) => {
	const selectedStatus = selectedStatusValue || DEFAULT_PSES_STATUS_TAB
	const queries = []
	let query = new Parse.Query("ProductionStepExecutions")
		.notEqualTo("deleted", true)
		.notEqualTo("isActive", false)
		.notEqualTo("productionStepSnapshot.transformation", "UNPACKING")
		.notEqualTo("productionStepSnapshot.transformation", "TECHNICAL")

	if (filters.productionDay) {
		const productionDayTimestamp = dayjs.utc(filters.productionDay).startOf("day").valueOf()
		const prevDayTimestamp = dayjs.utc(filters.productionDay).subtract(1, "day").startOf("day").valueOf()
		const nightRegex = "l[ae]?\\s*nuite?"

		const overnightBlock = Parse.Query.or(
			new Parse.Query("ProductionStepExecutions")
				.matches("productionStepSnapshot.description", nightRegex, "i"),
			new Parse.Query("ProductionStepExecutions")
				.matches("productionStepSnapshot.name", nightRegex, "i")
		)
		const parseQueryLastNight = Parse.Query.and(
			overnightBlock,
			new Parse.Query("ProductionStepExecutions")
				.equalTo("stepProductionDate", prevDayTimestamp)
				.notEqualTo("status", "DONE")
		)
		const queryDate = Parse.Query.or(
			new Parse.Query("ProductionStepExecutions")
				.equalTo("stepProductionDate", productionDayTimestamp),
			new Parse.Query("ProductionStepExecutions")
				.doesNotExist("stepProductionDate")
				.equalTo("productionDate", productionDayTimestamp),
			parseQueryLastNight
		)

		queries.push(queryDate)
	}

	if (filters.kitchenArea) {
		query.equalTo("productionStepSnapshot.kitchenArea", convertIdToPointer("KitchenArea", filters.kitchenArea.objectId))
	}

	switch (selectedSidebarId) {
		case PSES_SIDEBAR_MENUS_ENUM.WEIGHING:

			if (selectedStatus === "DONE") {
				query.containedIn("status", ["DONE", "TO_TEST"])
					.equalTo("productionStepSnapshot.endStepWeighing", true)
					.exists("netWeight")
			} else {
				query.equalTo("status", selectedStatus)
					.containedIn("subStatus", [PSE_SUBSTATUSES.weightValidation, PSE_SUBSTATUSES.insufficientWeight])
			}
			break
		case PSES_SIDEBAR_MENUS_ENUM.TASTING:
			if (selectedStatus === "TODO") {
				query.equalTo("status", "TO_TEST")
			} else {
				query.equalTo("status", "DONE")
					.equalTo("productionStepSnapshot.endStepWeighing", true)
					.equalTo("isSectionLastStep", true)
			}
			break
		// Initial page load where no menu is selected
		default:
			if (selectedStatus === "DONE") {
				let queryDone = Parse.Query.or(
					new Parse.Query("ProductionStepExecutions")
						.equalTo("status", "DONE"),
					new Parse.Query("ProductionStepExecutions")
						.equalTo("status", "TO_TEST")
						.equalTo("isSectionLastStep", true),
					new Parse.Query("ProductionStepExecutions")
						.containedIn("subStatus", [PSE_SUBSTATUSES.weightValidation, PSE_SUBSTATUSES.insufficientWeight])
						.equalTo("productionStepSnapshot.endStepWeighing", true)
				)
				queries.push(queryDone)
			} else {
				query.equalTo("status", selectedStatus)
				query.notContainedIn("subStatus", [PSE_SUBSTATUSES.insufficientWeight, PSE_SUBSTATUSES.weightValidation])
			}
			break
	}
	if (queries.length > 0) {
		query = Parse.Query.and(query, ...queries)
	}

	query.include(fieldsToInclude)
	return query
}

export const getPSEsFromQuery = async (query, toJSON = true) => {
	const pses = await query.limit(parseLimitRequest).find() || []
	const sortedMainPSEs = sortPSEByTimeFields(pses)

	return toJSON ? sortedMainPSEs.map(pse => pse.toJSON()) : sortedMainPSEs
}

export const getCountOfPSEsByQuery = async (query) => {
	const count = await query.count()
	return count
}

export const getPSEsByProductionDateToMerge = async (productionDate) => {
	const productionDateToTimestamp = moment.utc(productionDate).startOf("day").valueOf()

	const productionStepIds = []
	await new Parse.Query(ProductionStepExecution)
		.notEqualTo("deleted", true)
		.notEqualTo("isActive", false)
		.notEqualTo("isStepMerged", true)
		.equalTo("productionDate", productionDateToTimestamp)
		.each(pse => {
			const productionStep = pse.get("productionStep")
			if (productionStep && productionStep.id) {
				productionStepIds.push(productionStep.id)
			}
		})

	return productionStepIds
}

export const saveProductionStepsExecutionsWithHours = async (values) => {
	const ids = values.map(({ objectId }) => objectId)
	const productionStepExecutions = await new Parse.Query(ProductionStepExecution).containedIn("objectId", ids).find()

	const changedPSE = []

	for (const productionStepExecution of productionStepExecutions) {
		const productionStepExecutionValues = values.find(({ objectId }) => objectId === productionStepExecution.id)
		let changed = false

		// theoreticalStartTime
		if (PSE_REGEX_HOURS.test(productionStepExecutionValues.theoreticalStartTime)) {
			const startTimeToTimeStamp = editableHourToTimestamp(productionStepExecutionValues.theoreticalStartTime, productionStepExecution.get("stepProductionDate"))
			productionStepExecution.set("theoreticalStartTime", startTimeToTimeStamp)
			changed = true
		}
		// theoreticalEndTime
		if (PSE_REGEX_HOURS.test(productionStepExecutionValues.theoreticalEndTime)) {
			const endTimeToTimeStamp = editableHourToTimestamp(productionStepExecutionValues.theoreticalEndTime, productionStepExecution.get("stepProductionDate"))
			productionStepExecution.set("theoreticalEndTime", endTimeToTimeStamp)
			if (!changedPSE.includes(productionStepExecution)) {
				changed = true
			}
		}
		// machines
		if (productionStepExecutionValues.machines && productionStepExecutionValues.machines.length) {
			const machinesBatch = []
			for (const machine of productionStepExecutionValues.machines) {
				const machinePointer = await getMachinesById(machine, [], false)
				machinesBatch.push({ machine: machinePointer, machineName: machinePointer.get("name") })
			}
			productionStepExecution.set("machinesBatch", machinesBatch)
			changed = true
		}

		if (changed) {
			changedPSE.push(productionStepExecution)
		}
	}

	if (changedPSE.length) {
		await Parse.Object.saveAll(changedPSE)
	}
}

/** 
 * options.withSection: boolean - if true, include section data in the return object
 *  * @param sectionId
 * @param productionDate
 * @returns {Promise<*[]>}
 */
export const getAllPSEWithCommentsBySection = async (sectionId, productionDate, options = {}) => {
	try {
		const sectionQuery = new Parse.Query(Parse.Object.extend("Section"))
			.equalTo("objectId", sectionId)
		if (options.withSection) {
			sectionQuery.include(["productionSteps.step.productionSteps", "sectionOutputs"])
		}
		const section = await sectionQuery.first()
		const PSEComments = await new Parse.Query(ProductionStepExecution)
			.notEqualTo("deleted", true)
			.select("comments")
			.equalTo("section", section)
			.equalTo("productionDate", productionDate)
			.exists("comments")
			.limit(parseLimitRequest)
			.find() || []

		const PSECommentsJSON = PSEComments.map(PSEComment => PSEComment.toJSON())
		const comments = []
		PSECommentsJSON.forEach(PSEComment => {
			PSEComment.comments.forEach(comment => comments.push(comment))
		})

		const sortedComments = comments.sort((a, b) => b.creation_dt - a.creation_dt)
		if (options.withSection) {
			return { section: section.toJSON(), comments: sortedComments }
		}
		return sortedComments
	}
	catch (e) {
		console.log(e.message)
	}
}

export const getRecipeIdsByProductionStepExecution = async (productionDate) => {
	try {
		const productionStepExecutions = await new Parse.Query(ProductionStepExecution)
			.select("recipe")
			.notEqualTo("deleted", true)
			.equalTo("productionDate", productionDate)
			.exists("recipe")
			.limit(parseLimitRequest)
			.find() || []

		const uniqueRecipeIds = []
		const seen = new Set()
		for (const productionStepExecution of productionStepExecutions) {
			const recipeId = productionStepExecution.get("recipe").id
			if (!seen.has(recipeId)) {
				uniqueRecipeIds.push(recipeId)
				seen.add(recipeId)
			}
		}

		return uniqueRecipeIds
	} catch (e) {
		return Promise.reject(e)
	}
}


export const loadPSEByProductionDateAndSupplierItem = async ({
	productionDate,
	supplierItemId,
	toJSON = true,
	startProductionDate,
	endProductionDate
}) => {
	const productionStepExecutions = await Parse.Cloud.run("getPSEsBySupplierItemAndProductionDate", {
		productionDate,
		supplierItemId,
		startProductionDate,
		endProductionDate,
		toJson: toJSON
	})

	return productionStepExecutions || []
}


export const getUlteriorSectionPriorSteps = async (sectionOutputs, date) => {
	const productionStepQuery = new Parse.Query(Parse.Object.extend("ProductionStep"))
	const productionStepExecutionQuery = new Parse.Query(ProductionStepExecution)
	productionStepQuery.containedIn("stepComponents.sectionOutput.index", sectionOutputs.map(output => output.index))
	productionStepExecutionQuery.equalTo("stepProductionDate", date)
		.notEqualTo("deleted", true)
		.equalTo("status", PSE_STATUS.locked)
		.matchesQuery("productionStep", productionStepQuery)
		.include("priorSteps.step")
	return productionStepExecutionQuery.find()
}

export const getPseWithSectionOutputs = async (sectionOutputs, productionDate = null, recipeId = null) => {
	// Extract base indices, removing any suffix like "_0" or "_1"
	const indices = sectionOutputs.map(output => {
		const index = output.outPutIndex || output.index
		// Remove suffix pattern like "_0", "_1", etc.
		return index.replace(/_\d+$/, "")
	})
	
	const productionStepQuery = new Parse.Query(Parse.Object.extend("ProductionStep"))
		.containedIn("stepComponents.sectionOutput.index", indices)
	
	if (recipeId) productionStepQuery.equalTo("recipe", convertIdToPointer(COLLECTION_NAMES.recipe, recipeId))

	const pseQuery = new Parse.Query(ProductionStepExecution)
		.matchesQuery("productionStep", productionStepQuery)
		.matchesKeyInQuery("recipe", "recipe", productionStepQuery)
		.notEqualTo("deleted", true)
	if (productionDate)
		pseQuery.equalTo("productionDate", productionDate)

	return await pseQuery.first()
}

/**
 * Retrieve the name of a SupplierItem, preferring the value already
 * present in the stepComponent if available.
 *
 * Logic:
 * - If the stepComponent already has a supplierItem with a `name`, return it immediately.
 * - Otherwise, query the SupplierItems collection by objectId to fetch the name.
 */
export const getSupplierItemName = async (supplierId, stepComp) => {
	// 1. If stepComponent already contains supplierItem with a name, return it directly (no query needed)
	if (stepComp && stepComp.supplierItem && stepComp.supplierItem.name) return stepComp.supplierItem.name

	// 2. Otherwise, query SupplierItems collection by objectId
	try {
		const supplierItem = await new Parse.Query("SupplierItems")
			.equalTo("objectId", supplierId)
			.first()

		// 3. Return the name if found, otherwise null
		return supplierItem ? supplierItem.get("name") : null
	}
	catch (error) {
		console.log("Error fetching SupplierItem", supplierId, error)
		return null
	}
}


/**
 * Helper to retrieve PSEs from the day before a given date, with status !== 'DONE',
 * and description or name matching overnight regex pattern. Supports select and include for classic Parse queries.
 * @param {number} prevDayTimestamp - Timestamp for the previous day (start of day, UTC)
 * @param {Array<string>} fieldsToSelect - Fields to select (optional)
 * @param {Array<string>} fieldsToInclude - Fields to include (optional)
 * @param {number} limit - Max results (optional)
 * @returns {Promise<Array>} Array of matching PSEs
 */
async function getPrevDayNightPSEs({ prevDayTimestamp, fieldsToSelect = [], fieldsToInclude = [], limit = 100 }) {
	let query = new Parse.Query(ProductionStepExecution)
		.notEqualTo("deleted", true)
		.notEqualTo("isActive", false)
		.equalTo("stepProductionDate", prevDayTimestamp)
		.notEqualTo("status", "DONE")

	if (fieldsToInclude && fieldsToInclude.length) query = query.include(fieldsToInclude)
	if (fieldsToSelect && fieldsToSelect.length) query = query.select(fieldsToSelect)

	const allPrevDayPSEs = await query.limit(limit).find()

	// Use the utility function to filter PSEs matching overnight criteria
	const filteredPSEs = allPrevDayPSEs.filter(pse => isPSEFromOvernight(pse))

	return filteredPSEs
}
